import { DefaultMap, definedMap, mapFilterUndefined } from "@glideapps/ts-necessities";
import toPairs from "lodash/toPairs";
import uuid from "uuid";
import { extractInitials, tryShortenLinkToHostname } from "../../support/strings";
import { getFeatureFlag } from "../feature-flags";
import type { DateFormat, TimeFormat } from "../format-date-time";
import { formatDateTime } from "../format-date-time";
import { formatNumberFixed } from "../format-number";
import { GeneratedImageKind } from "../formula";
import { getDistanceFromLatLonInKm } from "../geometry";
import { GlideDateTime } from "../glide-date-time";
import { getWeekNumber, log2, modulo, round2, trunc2 } from "../math-functions";
import {
    assert,
    assertNever,
    convertGlideDurationToJS,
    convertJSDurationToGlide,
    defined,
    definedMapWithDefault,
    fetchPageMetadata,
    getNowTimeZoneAgnostic,
    isArray,
    makeEndOfDayTimeZoneAgnostic,
    makeStartOfDayTimeZoneAgnostic,
    normalizeEmailAddress,
    parseURL,
    tokenizeTemplateString,
} from "../Support";
import { makeSMSURL, makeTelephoneURL } from "../urls";
import { AppKind } from "./app-kind";
import type { MaybeForwardee } from "./component-helpers";
import {
    asArrayValue,
    asBoolean,
    asDate,
    asMaybeNumber,
    asMaybeString,
    asNumber,
    asString,
    AsyncRecomputingValueProducer,
    BaseForwardingValueProducer,
    ChainedEvalEnvironment,
    deconstructLatLong,
    FetchingValueProducer,
    ForwardingFromOneOrTwoValueProducer,
    ForwardingFromTwoValueProducer,
    ForwardingValueProducer,
    ImmutableSubscribableValueProducer,
    isLoadedDefined,
    isValueNotEmpty,
    loadedDefinedMap,
    loadedDefinedMapWithDefault,
    parseValueAsDate,
    parseValueAsDateSync,
    RecomputingFromListValueProducer,
    RecomputingFromObjectValueProducer,
    RecomputingFromOneOrTwoValueProducer,
    RecomputingFromOneValueProducer,
    RecomputingFromThreeOrFourValueProducer,
    RecomputingFromThreeValueProducer,
    RecomputingFromTwoValueProducer,
    RecomputingFromTwoWithDependenciesValueProducer,
    RecomputingValueProducer,
    TwoPhaseRecomputingValueProducer,
} from "./component-helpers";
import type {
    Context,
    DeserializableKind,
    DeserializationSpec,
    EnvironmentBoundEvaluable,
    Evaluable,
    ForceValueProducer,
    Loading,
    ObjectInspector,
    ObjectValue,
    PotentialValue,
    RootEvalEnvironment,
    Value,
    ValueListener,
    ValueProducer,
} from "./computation-types";
import { Callable, LOADING } from "./computation-types";
import { Constant, Data } from "./data";
import { registerDeserializable } from "./deserialization";
import type { Icon } from "./icons";
import type { DevStringKey, LocalizedStringKey } from "./localization";
import { formatLocalizedString, getAnyLocalizedString } from "./localization";
import type { BuiltinValueProducerClass } from "./primitives";
import {
    arePrimitivesEqual,
    areValuesEqual,
    BuiltinCallable,
    compareValues,
    EnvironmentBoundValueProducer,
} from "./primitives";
import { generateImageFromSeed } from "./triangle-image";

class FunctionCallable<O, A> extends Callable<O, A> {
    constructor(
        private readonly _contextName: string,
        private readonly _argNames: string[],
        private readonly _body: Evaluable<O, A>
    ) {
        super();
    }

    public call(
        args: EnvironmentBoundEvaluable<O, A>[],
        context: Context<O, A>,
        rootEnv: RootEvalEnvironment<O, A>
    ): ValueProducer<O, A> {
        const numArgs = args.length;
        assert(numArgs === this._argNames.length);
        const bindings = new Map<string, EnvironmentBoundEvaluable<O, A>>();
        bindings.set(this._contextName, new EnvironmentBoundValueProducer(context));
        for (let i = 0; i < numArgs; i++) {
            bindings.set(this._argNames[i], args[i]);
        }
        return this._body.evalInEnvironment(new ChainedEvalEnvironment(bindings, rootEnv), context);
    }

    static get spec(): DeserializationSpec<DeserializableKind> {
        return ["Function", "callable", ["contextName", "string"], ["argNames", "string-array"], ["body", "evaluable"]];
    }
}

const builtinFunctions = new Map<string, Callable<any, any>>();

export function registerBuiltinFunction<O, A>(name: string, builtin: Callable<O, A>): void {
    assert(!builtinFunctions.has(name), `Function ${name} registered more than once`);
    builtinFunctions.set(name, builtin);
}

class LookupBuiltinCallable<O, A> extends Callable<O, A> {
    private readonly _builtin: Callable<O, A>;

    constructor(name: string) {
        super();
        this._builtin = defined(builtinFunctions.get(name));
    }

    public call(
        args: EnvironmentBoundEvaluable<O, A>[],
        context: Context<O, A>,
        rootEnv: RootEvalEnvironment<O, A>
    ): ValueProducer<O, A> {
        return this._builtin.call(args, context, rootEnv);
    }

    static get spec(): DeserializationSpec<DeserializableKind> {
        return ["LookupBuiltin", "callable", ["name", "string"]];
    }
}

class AreIdenticalValueProducer<O, A> extends RecomputingFromTwoValueProducer<O, A> {
    protected recomputeFromInputs(one: Value<O, A>, two: Value<O, A>): PotentialValue<O, A> {
        return one === two;
    }
}

export class AreEqualValueProducer<O, A> extends RecomputingFromTwoValueProducer<O, A> {
    protected recomputeFromInputs(one: Value<O, A>, two: Value<O, A>): PotentialValue<O, A> {
        return areValuesEqual(one, two);
    }
}

class IsLessThanValueProducer<O, A> extends RecomputingFromTwoValueProducer<O, A> {
    protected recomputeFromInputs(one: Value<O, A>, two: Value<O, A>): PotentialValue<O, A> {
        const result = compareValues(one, two);
        if (result === undefined) return undefined;
        return result < 0;
    }
}

class IsGreaterThanValueProducer<O, A> extends RecomputingFromTwoValueProducer<O, A> {
    protected recomputeFromInputs(one: Value<O, A>, two: Value<O, A>): PotentialValue<O, A> {
        const result = compareValues(one, two);
        if (result === undefined) return undefined;
        return result > 0;
    }
}

class IsLessOrEqualToValueProducer<O, A> extends RecomputingFromTwoValueProducer<O, A> {
    protected recomputeFromInputs(one: Value<O, A>, two: Value<O, A>): PotentialValue<O, A> {
        const result = compareValues(one, two);
        if (result === undefined) return undefined;
        return result <= 0;
    }
}

class IsGreaterOrEqualToValueProducer<O, A> extends RecomputingFromTwoValueProducer<O, A> {
    protected recomputeFromInputs(one: Value<O, A>, two: Value<O, A>): PotentialValue<O, A> {
        const result = compareValues(one, two);
        if (result === undefined) return undefined;
        return result >= 0;
    }
}

class IsDefinedValueProducer<O, A> extends RecomputingFromOneValueProducer<O, A> {
    protected recomputeFromInput(input: Value<O, A>): PotentialValue<O, A> {
        return input !== undefined;
    }
}

class IsArrayValueProducer<O, A> extends RecomputingFromOneValueProducer<O, A> {
    protected recomputeFromInput(input: Value<O, A>): PotentialValue<O, A> {
        return Array.isArray(input);
    }
}

class IsNonEmptyArrayValueProducer<O, A> extends RecomputingFromOneValueProducer<O, A> {
    protected recomputeFromInput(input: Value<O, A>): PotentialValue<O, A> {
        return Array.isArray(input) && input.length > 0;
    }
}

class ArrayContainsValueProducer<O, A> extends RecomputingFromTwoWithDependenciesValueProducer<O, A> {
    protected recomputeFromInputs(
        array: Value<O, A>,
        item: Value<O, A>,
        force: ForceValueProducer<O, A>
    ): PotentialValue<O, A> {
        for (const v of asArrayValue(array)) {
            if (areValuesEqual(force(v), item) === true) return true;
        }
        return false;
    }
}

class ArraysOverlapValueProducer<O, A> extends RecomputingFromTwoWithDependenciesValueProducer<O, A> {
    protected recomputeFromInputs(
        left: Value<O, A>,
        right: Value<O, A>,
        force: ForceValueProducer<O, A>
    ): PotentialValue<O, A> {
        if (left === undefined || right === undefined) return false;

        let first = asArrayValue(left);
        let second = asArrayValue(right);
        if (first.length > second.length) {
            const tmp = first;
            first = second;
            second = tmp;
        }
        for (const yVP of second) {
            const y = force(yVP);
            for (const xVP of first) {
                if (areValuesEqual(force(xVP), y) === true) return true;
            }
        }
        return false;
    }
}

class ListLengthValueProducer<O, A> extends RecomputingFromOneValueProducer<O, A> {
    protected recomputeFromInput(input: Value<O, A>): PotentialValue<O, A> {
        if (input === undefined) return undefined;
        return asArrayValue(input).length;
    }
}

export class ListNthValueProducer<O, A> extends ForwardingFromTwoValueProducer<O, A> {
    protected recomputeForwardeeFromInputs(
        list: PotentialValue<O, A>,
        index: PotentialValue<O, A>
    ): ValueProducer<O, A> | Loading | undefined {
        if (list === LOADING || index === LOADING) return LOADING;
        const l = asArrayValue(list);
        const i = asNumber(index);
        return l[i];
    }
}

class ListNthLastValueProducer<O, A> extends ForwardingFromTwoValueProducer<O, A> {
    protected recomputeForwardeeFromInputs(
        list: PotentialValue<O, A>,
        index: PotentialValue<O, A>
    ): ValueProducer<O, A> | Loading | undefined {
        if (list === LOADING || index === LOADING) return LOADING;
        const l = asArrayValue(list);
        const i = asNumber(index);
        return l[l.length - 1 - i];
    }
}

class ListTakeValueProducer<O, A> extends RecomputingFromTwoValueProducer<O, A> {
    protected recomputeFromInputs(list: Value<O, A>, count: Value<O, A>): PotentialValue<O, A> {
        if (list === undefined || count === undefined) return undefined;
        const l = asArrayValue(list);
        const c = asNumber(count);
        return l.slice(0, c);
    }
}

class ListFromObjectValueProducer<O, A> extends RecomputingFromObjectValueProducer<O, A> {
    protected recomputeFromObject(inspector: ObjectInspector<O, A>): PotentialValue<O, A> {
        const o = inspector.getObjectValue();
        const list: ValueProducer<O, A>[] = [];
        for (const name of Object.keys(o)) {
            list.push(new Data({ name: new Data<O, A>(name), value: o[name] }));
        }
        return list;
    }
}

class ReverseListValueProducer<O, A> extends RecomputingFromOneValueProducer<O, A> {
    protected recomputeFromInput(obj: Value<O, A>): PotentialValue<O, A> {
        if (obj === undefined) return undefined;

        const l = Array.from(asArrayValue(obj));
        l.reverse();
        return l;
    }
}

class JoinListsValueProducer<O, A> extends RecomputingFromTwoValueProducer<O, A> {
    protected recomputeFromInputs(one: Value<O, A>, two: Value<O, A>): PotentialValue<O, A> {
        if (one === undefined && two === undefined) return undefined;

        const result: ValueProducer<O, A>[] = [];
        for (const x of asArrayValue(one)) {
            result.push(x);
        }
        for (const x of asArrayValue(two)) {
            result.push(x);
        }

        return result;
    }
}

export function cleanNumber(x: number): number | undefined {
    if (isNaN(x) || !isFinite(x)) return undefined;
    return x;
}

abstract class RandomMathValueProducer<O, A> extends RecomputingValueProducer<O, A> {
    protected abstract compute(): number;

    public recompute(_: ForceValueProducer<O, A>): PotentialValue<O, A> {
        return cleanNumber(this.compute());
    }
}

function makeRandomMathValueProducer<O, A>(f: () => number): BuiltinValueProducerClass<O, A> {
    return class extends RandomMathValueProducer<O, A> {
        protected compute = f;
    };
}

abstract class UnaryMathValueProducer<O, A> extends RecomputingFromOneValueProducer<O, A> {
    protected abstract compute(x: number): number;

    protected recomputeFromInput(x: Value<O, A>): PotentialValue<O, A> {
        const a = asMaybeNumber(x);
        if (a === undefined) return undefined;
        return cleanNumber(this.compute(a));
    }
}

function makeUnaryMathValueProducer<O, A>(f: (x: number) => number): BuiltinValueProducerClass<O, A> {
    return class extends UnaryMathValueProducer<O, A> {
        protected compute = f;
    };
}

class AddValueProducer<O, A> extends RecomputingFromTwoValueProducer<O, A> {
    protected recomputeFromInputs(first: Value<O, A>, second: Value<O, A>): PotentialValue<O, A> {
        const a = asMaybeNumber(first);
        const b = asMaybeNumber(second);
        if (a === undefined && b === undefined) return undefined;
        if (a === undefined) return b;
        if (b === undefined) return a;
        return cleanNumber(a + b);
    }
}

abstract class BinaryMathValueProducer<O, A> extends RecomputingFromTwoValueProducer<O, A> {
    protected abstract compute(x: number, y: number): number | undefined;

    protected recomputeFromInputs(first: Value<O, A>, second: Value<O, A>): PotentialValue<O, A> {
        const a = asMaybeNumber(first);
        const b = asMaybeNumber(second);
        if (a === undefined || b === undefined) return undefined;
        return definedMap(this.compute(a, b), cleanNumber);
    }
}

function makeBinaryMathValueProducer<O, A>(f: (x: number, y: number) => number): BuiltinValueProducerClass<O, A> {
    return class extends BinaryMathValueProducer<O, A> {
        protected compute = f;
    };
}

class SubtractValueProducer<O, A> extends RecomputingFromTwoValueProducer<O, A> {
    protected recomputeFromInputs(first: Value<O, A>, second: Value<O, A>): PotentialValue<O, A> {
        const a = asMaybeNumber(first);
        const b = asMaybeNumber(second);
        if (a === undefined) return undefined;
        if (b === undefined) return a;
        return cleanNumber(a - b);
    }
}

class SubtractFromConstantValueProducer<O, A> extends BinaryMathValueProducer<O, A> {
    protected compute(a: number, b: number): number {
        return a - b;
    }
}

class ModuloValueProducer<O, A> extends BinaryMathValueProducer<O, A> {
    protected compute(x: number, y: number): number | undefined {
        return modulo(x, y);
    }
}

class RoundValueProducer<O, A> extends RecomputingFromOneOrTwoValueProducer<O, A> {
    protected recomputeFromInputs(nValue: Value<O, A>, digitsValue: Value<O, A>): PotentialValue<O, A> {
        const n = asMaybeNumber(nValue);
        if (n === undefined) return undefined;
        const digits = asMaybeNumber(digitsValue) ?? 0;
        return round2(n, digits);
    }
}

class TruncateValueProducer<O, A> extends RecomputingFromOneOrTwoValueProducer<O, A> {
    protected recomputeFromInputs(nValue: Value<O, A>, digitsValue: Value<O, A>): PotentialValue<O, A> {
        const n = asMaybeNumber(nValue);
        if (n === undefined) return undefined;
        const digits = asMaybeNumber(digitsValue) ?? 0;
        return trunc2(n, digits);
    }
}

class LogarithmValueProducer<O, A> extends RecomputingFromOneOrTwoValueProducer<O, A> {
    protected recomputeFromInputs(xValue: Value<O, A>, baseValue: Value<O, A>): PotentialValue<O, A> {
        const x = asMaybeNumber(xValue);
        if (x === undefined || x <= 0) return undefined;
        const base = asMaybeNumber(baseValue) ?? 10;
        return log2(x, base);
    }
}

class SubtractDatesValueProducer<O, A> extends RecomputingFromTwoValueProducer<O, A> {
    protected recomputeFromInputs(left: Value<O, A>, right: Value<O, A>): PotentialValue<O, A> {
        const leftDate = asDate(left);
        if (leftDate === undefined) return undefined;
        const rightDate = asDate(right);
        if (rightDate === undefined) return undefined;

        return convertJSDurationToGlide(leftDate.getTime() - rightDate.getTime());
    }
}

class AddDurationToDateValueProducer<O, A> extends RecomputingFromTwoValueProducer<O, A> {
    protected recomputeFromInputs(dateValue: Value<O, A>, duration: Value<O, A>): PotentialValue<O, A> {
        const date = asDate(dateValue);
        if (date === undefined || duration === undefined) return undefined;

        return new Date(date.getTime() + convertGlideDurationToJS(asNumber(duration)));
    }
}

class AndValueProducer<O, A> extends ImmutableSubscribableValueProducer<O, A> implements ValueListener<O, A> {
    private _current: boolean | undefined;
    private _subscribedToRight = false;

    constructor(
        _appEnvironment: A,
        private readonly _left: ValueProducer<O, A>,
        private readonly _right: ValueProducer<O, A>
    ) {
        super();
    }

    protected firstSubscribed(): void {
        super.firstSubscribed();

        this._left.subscribe(this);
        assert(!this._subscribedToRight);
    }

    protected lastUnsubscribed(): void {
        super.lastUnsubscribed();

        this._current = undefined;

        this._left.unsubscribe(this);
        if (this._subscribedToRight) {
            this._right.unsubscribe(this);
            this._subscribedToRight = false;
        }
    }

    public get current(): PotentialValue<O, A> {
        if (this._current !== undefined) {
            assert(this.hasSubscribers);
            return this._current;
        }

        const left = this._left.current;
        if (left === LOADING) return LOADING;

        if (left !== true) {
            if (this._subscribedToRight) {
                this._right.unsubscribe(this);
                this._subscribedToRight = false;
            }
            this._current = false;
            return this._current;
        }

        if (this.hasSubscribers && !this._subscribedToRight) {
            this._right.subscribe(this);
            this._subscribedToRight = true;
        }

        const right = this._right.current;
        if (right === LOADING) return LOADING;

        this._current = right === true;
        return this._current;
    }

    public setDirty(): void {
        this._current = undefined;
        this.setListenersDirty();
    }

    public getObjectInspector(): ObjectInspector<O, A> | Loading | undefined {
        return undefined;
    }
}

class OrValueProducer<O, A> extends ImmutableSubscribableValueProducer<O, A> implements ValueListener<O, A> {
    private _current: boolean | undefined;
    private _subscribedToRight = false;

    constructor(
        _appEnvironment: A,
        private readonly _left: ValueProducer<O, A>,
        private readonly _right: ValueProducer<O, A>
    ) {
        super();
    }

    protected firstSubscribed(): void {
        super.firstSubscribed();

        this._left.subscribe(this);
        assert(!this._subscribedToRight);
    }

    protected lastUnsubscribed(): void {
        super.lastUnsubscribed();

        this._current = undefined;

        this._left.unsubscribe(this);
        if (this._subscribedToRight) {
            this._right.unsubscribe(this);
            this._subscribedToRight = false;
        }
    }

    public get current(): PotentialValue<O, A> {
        if (this._current !== undefined) {
            assert(this.hasSubscribers);
            return this._current;
        }

        const left = this._left.current;
        if (left === LOADING) return LOADING;

        if (left === true) {
            if (this._subscribedToRight) {
                this._right.unsubscribe(this);
                this._subscribedToRight = false;
            }
            this._current = true;
            return this._current;
        }

        if (this.hasSubscribers && !this._subscribedToRight) {
            this._right.subscribe(this);
            this._subscribedToRight = true;
        }

        const right = this._right.current;
        if (right === LOADING) return LOADING;

        this._current = right === true;
        return this._current;
    }

    public setDirty(): void {
        this._current = undefined;
        this.setListenersDirty();
    }

    public getObjectInspector(): ObjectInspector<O, A> | Loading | undefined {
        return undefined;
    }
}

class StringToNumberValueProducer<O, A> extends RecomputingFromOneValueProducer<O, A> {
    protected recomputeFromInput(input: Value<O, A>): PotentialValue<O, A> {
        if (input === undefined) return undefined;
        return parseFloat(asString(input));
    }
}

class GetLocalizedStringValueProducer<O, A> extends RecomputingFromOneValueProducer<O, A> {
    protected recomputeFromInput(input: Value<O, A>): PotentialValue<O, A> {
        if (input === undefined) return undefined;
        return getAnyLocalizedString(asString(input) as LocalizedStringKey | DevStringKey, AppKind.App);
    }
}

class FormatLocalizedStringValueProducer<O, A> extends RecomputingFromTwoWithDependenciesValueProducer<O, A> {
    protected recomputeFromInputs(
        name: Value<O, A>,
        strings: Value<O, A>,
        force: ForceValueProducer<O, A>
    ): PotentialValue<O, A> {
        if (name === undefined || strings === undefined) return undefined;

        return formatLocalizedString(
            asString(name) as LocalizedStringKey,
            asArrayValue(strings).map(vp => {
                return loadedDefinedMapWithDefault(force(vp), "", asString);
            })
        );
    }
}

class NotValueProducer<O, A> extends RecomputingFromOneOrTwoValueProducer<O, A> {
    protected recomputeFromInputs(input: Value<O, A>, fallback: Value<O, A>): PotentialValue<O, A> {
        if (input === undefined) return fallback;
        return !asBoolean(input);
    }
}

class WithDefaultValueProducer<O, A> extends BaseForwardingValueProducer<O, A> {
    private _isValid: boolean | Loading = LOADING;
    private _subscribedToDefault: boolean = false;

    constructor(
        _appEnvironment: A,
        private readonly _value: ValueProducer<O, A>,
        private readonly _defaultValue: ValueProducer<O, A>
    ) {
        super();
    }

    protected firstSubscribed(): void {
        super.firstSubscribed();

        assert(this._isValid === LOADING && !this._subscribedToDefault);

        this._value.subscribe(this);
    }

    protected lastUnsubscribed(): void {
        super.lastUnsubscribed();

        this._value.unsubscribe(this);
        if (this._subscribedToDefault) {
            this._defaultValue.unsubscribe(this);
            this._subscribedToDefault = false;
        }
    }

    public getCurrentForwardee(): MaybeForwardee<O, A> {
        if (this._isValid === LOADING) {
            const value = this._value.current;
            // If the value is LOADING, we forward that, instead of forwarding
            // the default.
            this._isValid = value !== undefined;
            if (this._isValid) {
                if (this._subscribedToDefault) {
                    this._defaultValue.unsubscribe(this);
                    this._subscribedToDefault = false;
                }
            } else {
                if (!this._subscribedToDefault) {
                    this._defaultValue.subscribe(this);
                    this._subscribedToDefault = true;
                }
            }
        }

        if (this._isValid === true) {
            return this._value;
        } else if (this._isValid === false) {
            return this._defaultValue;
        } else {
            return assertNever(this._isValid);
        }
    }

    public setDirty(sender: ValueProducer<O, A>): void {
        if (sender === this._value) {
            this._isValid = LOADING;
        }
        this.setListenersDirty();
    }
}

// The primitive diode forwards an input value (defaulting to a default
// value), but allows overwriting the copy.  When the input updates, the copy
// is overwritten.  We use this in input components to have a "scratchpad"
// that the component can modify and then call the "set columns" action on.
//
// This is the third implementation - the past two were wrong.  One tricky
// thing is that is has to keep its overwritten value even when it has no
// subscribers, and when subscribers come in again it must not overwrite it
// with the input unless the input changed in the mean time. The way we handle
// this is that we keep a `lastValue`, which is the last value we copied from
// the input, and we only re-copy the input if it's different from that value,
// which means that the logic is independent of whether anybody's subscribed
// or not.
//
// NOTE: This is only supposed to work on primitives!
class PrimitiveDiodeValueProducer<O, A> extends ForwardingFromOneOrTwoValueProducer<O, A> {
    private _copy: { data: Data<O, A>; lastValue: Value<O, A> } | undefined;

    protected shouldOverwrite(): boolean {
        return true;
    }

    protected recomputeForwardeeFromInputs(
        inputValue: PotentialValue<O, A>,
        defaultValue: PotentialValue<O, A>
    ): ValueProducer<O, A> | Loading | undefined {
        if (inputValue === LOADING) {
            return this._copy?.data ?? LOADING;
        }

        const value = inputValue ?? defaultValue;
        if (value === LOADING) {
            return this._copy?.data ?? LOADING;
        }

        if (this._copy === undefined || (this.shouldOverwrite() && !arePrimitivesEqual(this._copy.lastValue, value))) {
            this._copy = { data: new Data<O, A>(value), lastValue: value };
        }
        return this._copy.data;
    }
}

class WriteOncePrimitiveDiodeValueProducer<O, A> extends PrimitiveDiodeValueProducer<O, A> {
    private _didWrite: boolean = false;

    protected shouldOverwrite(): boolean {
        return !this._didWrite;
    }

    public set(v: Value<O, A>): boolean {
        this._didWrite = true;
        return super.set(v);
    }
}

class ConvertToBooleanValueProducer<O, A>
    extends ImmutableSubscribableValueProducer<O, A>
    implements ValueListener<O, A>
{
    constructor(_appEnvironment: A, private readonly _input: ValueProducer<O, A>) {
        super();
    }

    protected firstSubscribed(): void {
        super.firstSubscribed();
        this._input.subscribe(this);
    }

    protected lastUnsubscribed(): void {
        super.lastUnsubscribed();
        this._input.unsubscribe(this);
    }

    public setDirty(): void {
        this.setListenersDirty();
    }

    public get current(): PotentialValue<O, A> {
        return loadedDefinedMap(this._input.current, asBoolean);
    }

    public set(v: Value<O, A>): boolean {
        return this._input.set(asBoolean(v));
    }

    public getObjectInspector(): ObjectInspector<O, A> | Loading | undefined {
        return undefined;
    }
}

class ConvertToNumberValueProducer<O, A> extends RecomputingFromOneValueProducer<O, A> {
    protected recomputeFromInput(input: Value<O, A>): PotentialValue<O, A> {
        if (input === undefined) return undefined;
        const n = asMaybeNumber(input);
        if (n === undefined || isNaN(n)) return undefined;
        return n;
    }
}

class ConvertToStringValueProducer<O, A> extends RecomputingFromOneValueProducer<O, A> {
    protected recomputeFromInput(input: Value<O, A>): PotentialValue<O, A> {
        if (input === undefined) return undefined;
        return asMaybeString(input);
    }
}

// ##convertingRelationKeys:
// This is how the old computation model converts primitive values to strings
// when they are keys for relations.
class ConvertToLocaleAgnosticStringValueProducer<O, A> extends RecomputingFromOneValueProducer<O, A> {
    protected recomputeFromInput(x: Value<O, A>): PotentialValue<O, A> {
        if (typeof x === "string") return x;
        if (typeof x === "number") return x.toString();
        if (typeof x === "boolean") return x.toString();
        if (x instanceof Date) return x.toISOString();
        if (x instanceof GlideDateTime) return x.asString();
        if (x === undefined) return "";
        return undefined;
    }
}

class ConvertToDateTimeValueProducer<O, A> extends RecomputingFromOneValueProducer<O, A> {
    protected recomputeFromInput(input: Value<O, A>): PotentialValue<O, A> {
        if (input === undefined) return undefined;
        return asDate(input);
    }
}

class ParseUserDateTimeValueProducer<O, A> extends AsyncRecomputingValueProducer<O, A> implements ValueListener<O, A> {
    constructor(_appEnvironment: A, private readonly _input: ValueProducer<O, A>) {
        super();
    }

    protected firstSubscribed(): void {
        super.firstSubscribed();
        this._input.subscribe(this);
    }

    protected lastUnsubscribed(): void {
        super.lastUnsubscribed();
        this._input.unsubscribe(this);
    }

    protected recomputeAsync(): PotentialValue<O, A> | Promise<PotentialValue<O, A>> {
        const input = this._input.current;
        if (!isLoadedDefined(input)) return input;
        const maybeDate = parseValueAsDateSync(input);
        if (maybeDate !== undefined) return maybeDate;
        return parseValueAsDate(input);
    }

    public set(v: Value<O, A>): boolean {
        return this._input.set(v);
    }

    public getObjectInspector(): ObjectInspector<O, A> | Loading | undefined {
        return undefined;
    }
}

class DecomposeDateTimeValueProducer<O, A> extends RecomputingFromOneValueProducer<O, A> {
    protected recomputeFromInput(input: Value<O, A>): PotentialValue<O, A> {
        const d = asDate(input);
        if (d === undefined) return undefined;

        return {
            year: new Constant<O, A>(d.getUTCFullYear()),
            month: new Constant<O, A>(d.getUTCMonth() + 1),
            day: new Constant<O, A>(d.getUTCDate()),
            hour: new Constant<O, A>(d.getUTCHours()),
            minute: new Constant<O, A>(d.getUTCMinutes()),
            second: new Constant<O, A>(d.getUTCSeconds()),
            weekday: new Constant<O, A>(d.getUTCDay() + 1),
            weekNumber: new Constant<O, A>(getWeekNumber(d)),
        };
    }
}

class ContainsStringValueProducer<O, A> extends RecomputingFromTwoValueProducer<O, A> {
    protected recomputeFromInputs(haystack: Value<O, A>, needle: Value<O, A>): PotentialValue<O, A> {
        return asString(haystack).toLowerCase().includes(asString(needle).toLowerCase());
    }
}

class StringsAreLowercaseTrimEqualValueProducer<O, A> extends RecomputingFromTwoValueProducer<O, A> {
    protected recomputeFromInputs(one: Value<O, A>, two: Value<O, A>): PotentialValue<O, A> {
        function transformString(s: Value<O, A>) {
            return asString(s).toLowerCase().trim();
        }

        return definedMapWithDefault(one, "", transformString) === definedMapWithDefault(two, "", transformString);
    }
}

class AreEmailsEqualValueProducer<O, A> extends RecomputingFromTwoValueProducer<O, A> {
    protected recomputeFromInputs(first: Value<O, A>, second: Value<O, A>): PotentialValue<O, A> {
        const firstEmail = normalizeEmailAddress(asString(first));
        const secondEmail = normalizeEmailAddress(asString(second));
        return firstEmail === secondEmail;
    }
}

class MatchesEmailFilterValueProducer<O, A> extends RecomputingFromTwoWithDependenciesValueProducer<O, A> {
    protected recomputeFromInputs(
        userEmailValue: Value<O, A>,
        dataEmailValue: Value<O, A>,
        force: ForceValueProducer<O, A>
    ): PotentialValue<O, A> {
        function transformString(s: Value<O, A>) {
            return normalizeEmailAddress(asString(s));
        }

        const userEmail = definedMapWithDefault(userEmailValue, "", transformString);

        function emailAddressOrMatch(s: PotentialValue<O, A>): boolean | string {
            if (s === LOADING) return false;
            // When there's no email in the row, it gets shown to everybody
            if (s === undefined) return true;
            if (typeof s !== "string") return false;

            const email = transformString(s);
            if (email === "") return true;
            return email;
        }

        if (dataEmailValue === undefined) return true;

        if (Array.isArray(dataEmailValue)) {
            return (
                dataEmailValue.every(vp => emailAddressOrMatch(force(vp)) === true) ||
                dataEmailValue.some(vp => emailAddressOrMatch(force(vp)) === userEmail)
            );
        } else {
            const email = emailAddressOrMatch(dataEmailValue);
            if (typeof email === "boolean") return email;
            return userEmail === email;
        }
    }
}

class SplitStringValueProducer<O, A> extends RecomputingFromTwoValueProducer<O, A> {
    protected recomputeFromInputs(string: Value<O, A>, separator: Value<O, A>): PotentialValue<O, A> {
        if (string === undefined || separator === undefined) return undefined;

        return asString(string)
            .split(asString(separator))
            .map(s => s.trim())
            .filter(s => s !== "")
            .map(s => new Constant<O, A>(s));
    }
}

class ExtractInitialsValueProducer<O, A> extends RecomputingFromOneValueProducer<O, A> {
    protected recomputeFromInput(input: Value<O, A>): PotentialValue<O, A> {
        if (input === undefined) return undefined;
        return extractInitials(asString(input));
    }
}

class JoinStringsValueProducer<O, A> extends RecomputingFromListValueProducer<O, A> {
    protected recomputeFromList(
        list: ReadonlyArray<ValueProducer<O, A>>,
        force: ForceValueProducer<O, A>
    ): PotentialValue<O, A> {
        const strings: string[] = [];
        for (const vp of list) {
            const s = force(vp);
            if (s === LOADING) return LOADING;
            strings.push(asString(s));
        }
        return strings.join("");
    }
}

function forceReplacements<O, A>(
    replacementsArray: readonly ValueProducer<O, A>[],
    force: ForceValueProducer<O, A>
): { patterns: readonly string[]; replacements: Map<string, ValueProducer<O, A>> } {
    const patterns: string[] = [];
    const replacements = new Map<string, ValueProducer<O, A>>();

    for (let i = 0; i < replacementsArray.length; i += 2) {
        const pattern = loadedDefinedMap(force(replacementsArray[i + 0]), asString);
        const replacement = replacementsArray[i + 1];

        if (!isLoadedDefined(pattern) || pattern === "") continue;

        patterns.push(pattern);
        replacements.set(pattern, replacement);
    }

    return { patterns, replacements };
}

class ReplaceStringsValueProducer<O, A> extends RecomputingFromTwoWithDependenciesValueProducer<O, A> {
    protected recomputeFromInputs(
        templateValue: Value<O, A>,
        replacementsValue: Value<O, A>,
        force: ForceValueProducer<O, A>
    ): PotentialValue<O, A> {
        if (templateValue === undefined || replacementsValue === undefined) return undefined;

        const { patterns, replacements } = forceReplacements(asArrayValue(replacementsValue), force);

        const template = asString(templateValue);
        if (patterns.length === 0) return template;

        const tokens = tokenizeTemplateString(template, patterns);
        const parts: string[] = [];

        for (const t of tokens) {
            if (t.isText) {
                parts.push(t.text);
            } else {
                const replacement = force(defined(replacements.get(t.value)));
                if (!isLoadedDefined(replacement)) continue;

                parts.push(asString(replacement));
            }
        }

        return parts.join("");
    }
}

class StringEmptyValueProducer<O, A> extends RecomputingFromOneValueProducer<O, A> {
    protected recomputeFromInput(input: Value<O, A>): PotentialValue<O, A> {
        return input === "";
    }
}

class IsNotEmptyValueProducer<O, A> extends RecomputingFromOneValueProducer<O, A> {
    protected recomputeFromInput(input: Value<O, A>): PotentialValue<O, A> {
        return isValueNotEmpty(input);
    }
}

class StringsNotEmptyValueProducer<O, A> extends RecomputingFromListValueProducer<O, A> {
    protected recomputeFromList(
        list: ReadonlyArray<ValueProducer<O, A>>,
        force: ForceValueProducer<O, A>
    ): PotentialValue<O, A> {
        if (list === undefined) return undefined;

        for (const vp of list) {
            const s = force(vp);
            if (typeof s !== "string") return false;
            if (s.length === 0) return false;
        }
        return true;
    }
}

class StringsAreNumbersOrEmptyValueProducer<O, A> extends RecomputingFromListValueProducer<O, A> {
    protected recomputeFromList(
        list: ReadonlyArray<ValueProducer<O, A>>,
        force: ForceValueProducer<O, A>
    ): PotentialValue<O, A> {
        if (list === undefined) return undefined;

        for (const vp of list) {
            const s = force(vp);
            if (typeof s !== "string") return false;
            if (s.length === 0) continue;
            if (isNaN(s as any)) return false;
        }
        return true;
    }
}

class TrimStringValueProducer<O, A> extends RecomputingFromOneValueProducer<O, A> {
    protected recomputeFromInput(input: Value<O, A>): PotentialValue<O, A> {
        if (input === undefined) return undefined;
        return asString(input).trim();
    }
}

class ObjectHasNonEmptyPropertiesValueProducer<O, A> extends RecomputingFromObjectValueProducer<O, A> {
    constructor(
        appEnvironment: A,
        obj: ValueProducer<O, A>,
        private readonly _properties: ValueProducer<O, A> | undefined
    ) {
        super(appEnvironment, obj);
    }

    protected recomputeFromObject(
        inspector: ObjectInspector<O, A>,
        force: ForceValueProducer<O, A>
    ): PotentialValue<O, A> {
        let properties: string[] | undefined;
        if (this._properties !== undefined) {
            const arr = force(this._properties);
            if (arr === LOADING) return LOADING;

            properties = [];
            if (isArray(arr)) {
                for (const vp of arr) {
                    const v = force(vp);
                    if (v === LOADING) return LOADING;
                    const str = asMaybeString(v);
                    if (str !== undefined) {
                        properties.push(str);
                    }
                }
            }
        }

        // FIXME: Shouldn't we return LOADING only if all properties
        // are either empty or LOADING?  I.e. if there's a non-empty
        // one the result shouldn't be LOADING?
        for (const key of properties ?? Object.keys(inspector.getObjectValue())) {
            const notEmpty = definedMap(inspector.getProperty(key), vp => isValueNotEmpty(force(vp))) ?? false;
            if (notEmpty === false) continue;
            return notEmpty;
        }
        return false;
    }
}

class AllObjectPropertiesAreTrueValueProducer<O, A> extends RecomputingFromObjectValueProducer<O, A> {
    protected recomputeFromObject(
        inspector: ObjectInspector<O, A>,
        force: ForceValueProducer<O, A>
    ): PotentialValue<O, A> {
        const obj = inspector.getObjectValue();
        for (const key of Object.keys(obj)) {
            const v = force(obj[key]);
            if (v === LOADING) return LOADING;
            if (v !== true) return false;
        }
        return true;
    }
}

export class PropertiesContainStringValueProducer<O, A> extends TwoPhaseRecomputingValueProducer<O, A> {
    constructor(
        _appEnvironment: A,
        private readonly _obj: ValueProducer<O, A>,
        private readonly _properties: ValueProducer<O, A>,
        private readonly _needle: ValueProducer<O, A>
    ) {
        super();
    }

    protected recomputeValueProducers(
        force: ForceValueProducer<O, A>
    ): readonly ValueProducer<O, A>[] | Loading | undefined {
        const inspector = this._obj.getObjectInspector(force);
        const properties = force(this._properties);

        if (inspector === LOADING || properties === LOADING) return LOADING;
        if (inspector === undefined || properties === undefined) return undefined;

        const vps: ValueProducer<O, A>[] = [this._needle];

        let isLoading = false;

        for (const propertyVP of asArrayValue(properties)) {
            const p = force(propertyVP);
            if (p === LOADING) {
                isLoading = true;
                continue;
            }
            if (typeof p !== "string") continue;

            const vp = inspector.getProperty(p);
            if (vp === undefined) continue;

            vps.push(vp);
        }

        if (vps.length === 1 && isLoading) return LOADING;
        return vps;
    }

    protected recomputeFromValueProducers(vps: readonly ValueProducer<O, A>[]): PotentialValue<O, A> {
        const needle = vps[0].current;
        if (!isLoadedDefined(needle)) return needle;

        const needleString = asString(needle).toLowerCase();

        if (needleString === "") return true;

        let isLoading = false;

        const len = vps.length;
        for (let i = 1; i < len; i++) {
            const v = vps[i].current;

            if (v === LOADING) {
                isLoading = true;
                continue;
            }

            if (v === undefined) continue;

            if (asString(v).toLowerCase().includes(needleString)) {
                return true;
            }
        }

        if (isLoading) return LOADING;
        return false;
    }

    public setDirty(sender: ValueProducer<O, A>): void {
        if (sender === this._needle) {
            this.setValuesDirty();
        } else {
            this.setDependenciesDirty();
        }
    }
}

class ExtractChangedPropertiesValueProducer<O, A> extends RecomputingValueProducer<O, A> {
    constructor(
        _appEnvironment: A,
        private readonly _originalObject: ValueProducer<O, A>,
        private readonly _newObject: ValueProducer<O, A>,
        private readonly _columnNames: ValueProducer<O, A> | undefined
    ) {
        super();
    }

    public recompute(force: ForceValueProducer<O, A>): PotentialValue<O, A> {
        const originalInspector = this._originalObject.getObjectInspector(force);
        const newInspector = this._newObject.getObjectInspector(force);

        if (!isLoadedDefined(originalInspector)) return originalInspector;
        if (!isLoadedDefined(newInspector)) return newInspector;

        const columnNames = definedMap(this._columnNames, force);
        if (columnNames === LOADING) return LOADING;

        const columnNamesSet = definedMap(columnNames, n => new Set(asArrayValue(n).map(vp => asString(vp.current))));

        const changes: ObjectValue<O, A> = {};

        for (const [key, newVP] of toPairs(newInspector.getObjectValue())) {
            if (columnNamesSet !== undefined && !columnNamesSet.has(key)) continue;

            const newProperty = force(newVP);

            if (newProperty === undefined) continue;
            if (newProperty !== LOADING) {
                const originalProperty = definedMap(originalInspector.getProperty(key), force);
                if (newProperty === originalProperty) continue;
            }

            changes[key] = newVP;
        }

        return changes;
    }
}

// FIXME: This is a memory leak, but we hope it's fine for the time being
const shuffleKeys: DefaultMap<string, Map<ValueProducer<any, any>, number>> = new DefaultMap(() => new Map());

const randomValueProducers: Set<ValueListener<unknown, unknown>> = new Set();

export function reshuffleKeys<O, A>(sender: ValueProducer<O, A>) {
    shuffleKeys.clear();

    for (const sp of randomValueProducers) {
        sp.setDirty(sender);
    }
}

class ShuffleValueProducer<O, A> extends RecomputingFromListValueProducer<O, A> {
    constructor(
        appEnvironment: A | undefined,
        list: ValueProducer<O, A>,
        private readonly _uniqueID: ValueProducer<O, A>
    ) {
        super(appEnvironment, list);
    }

    public recomputeFromList(
        list: readonly ValueProducer<O, A>[],
        force: ForceValueProducer<O, A>
    ): PotentialValue<O, A> {
        const uniqueID = loadedDefinedMap(force(this._uniqueID), asString);
        const keys = definedMap(uniqueID, id => shuffleKeys.get(id));

        const newKeys = new Map<ValueProducer<O, A>, number>();

        let itemsAndKeys: [ValueProducer<O, A>, number][] = [];
        for (const item of list) {
            let key = newKeys.get(item);
            if (key === undefined) {
                key = definedMap(keys, k => k.get(item));
                if (key === undefined) {
                    key = Math.random();
                }
                newKeys.set(item, key);
            }

            itemsAndKeys.push([item, key]);
        }

        if (uniqueID !== undefined) {
            shuffleKeys.set(uniqueID, newKeys);
        }

        itemsAndKeys = itemsAndKeys.sort(([, one], [, two]) => one - two);

        return itemsAndKeys.map(([i]) => i);
    }

    protected firstSubscribed() {
        super.firstSubscribed();
        randomValueProducers.add(this);
    }

    protected lastUnsubscribed() {
        super.lastUnsubscribed();
        randomValueProducers.delete(this);
    }

    public appendItem(vp: ValueProducer<O, A>): void {
        this._container.appendItem(vp);
    }

    public removeLastItem(): void {
        this._container.removeLastItem();
    }
}

let mockedRandomPickIndex: number | undefined;
export function mockOldComputationModelRandomPick(index: number | undefined): void {
    mockedRandomPickIndex = index;
}

class RandomPickValueProducer<O, A> extends ForwardingFromTwoValueProducer<O, A> {
    protected recomputeForwardeeFromInputs(
        listValue: PotentialValue<O, A>,
        uniqueIDValue: PotentialValue<O, A>
    ): ValueProducer<O, A> | Loading | undefined {
        if (!isLoadedDefined(listValue)) return listValue;
        const list = asArrayValue(listValue);
        if (list.length === 0) return undefined;

        if (mockedRandomPickIndex !== undefined) {
            return defined(list[mockedRandomPickIndex % list.length]);
        }

        const uniqueID = loadedDefinedMap(uniqueIDValue, asString);
        if (uniqueID === undefined) return undefined;

        const keys = shuffleKeys.get(uniqueID);
        let key = keys.get(this.first);
        if (key === undefined) {
            key = Math.random();
            keys.set(this.first, key);
        }

        const index = Math.floor(key * list.length);
        return defined(list[index]);
    }

    protected firstSubscribed() {
        super.firstSubscribed();
        randomValueProducers.add(this);
    }

    protected lastUnsubscribed() {
        super.lastUnsubscribed();
        randomValueProducers.delete(this);
    }
}

const pageTitlePromises: Map<string, Promise<Value<any, any>>> = new Map();

class PageTitleValueProducer<O, A> extends FetchingValueProducer<O, A, string> {
    constructor(url: string) {
        super(pageTitlePromises, url);
    }

    protected async fetch(url: string): Promise<Value<O, A>> {
        let title = tryShortenLinkToHostname(url);

        const response = await fetchPageMetadata(url, getFeatureFlag("useLocalPageMetadataService"));
        if (response !== undefined) {
            let maybeTitle = response.title;
            if (isLoadedDefined(maybeTitle)) {
                maybeTitle = maybeTitle.trim();
                if (maybeTitle.length > 0) {
                    title = maybeTitle;
                }
            }
        }

        return title;
    }

    public getObjectInspector(): ObjectInspector<O, A> | Loading | undefined {
        return undefined;
    }
}

interface LinkRecognizer {
    kind: string;
    title?: (match: RegExpMatchArray) => string;
    caption: string;
    icon: Icon;
    regex: RegExp;
}

const youTubeRegex = /^(?:https?:\/\/)?(?:www\.)?youtu\.?be(?:\.com)?\/?.*(?:watch|embed)?(?:.*v=|v\/|\/)([\w\-_]+)&?/;
const linkRecognizers: LinkRecognizer[] = [
    {
        kind: "youTube",
        caption: "YouTube",
        icon: "youtube",
        regex: youTubeRegex,
    },
    { kind: "wikipedia", caption: "Wikipedia", icon: "globe", regex: /^(?:https?:\/\/)?(?:[^./]+\.)?wikipedia\.org/ },
    { kind: "twitter", caption: "Twitter", icon: "twitter", regex: /^(?:https?:\/\/)?(?:[^./]+\.)?twitter\.com/ },
    { kind: "github", caption: "GitHub", icon: "github", regex: /^(?:https?:\/\/)?(?:[^./]+\.)?github\.com/ },
    { kind: "gitlab", caption: "GitLab", icon: "gitlab", regex: /^(?:https?:\/\/)?(?:[^./]+\.)?gitlab\.com/ },
    { kind: "facebook", caption: "Facebook", icon: "facebook", regex: /^(?:https?:\/\/)?(?:[^./]+\.)?facebook\.com/ },
    { kind: "linkedin", caption: "LinkedIn", icon: "linkedin", regex: /^(?:https?:\/\/)?(?:[^./]+\.)?linkedin\.com/ },
    { kind: "codepen", caption: "CodePen", icon: "codepen", regex: /^(?:https?:\/\/)?(?:[^./]+\.)?codepen\.com/ },
    { kind: "slack", caption: "Slack", icon: "slack", regex: /^(?:https?:\/\/)?(?:[^./]+\.)?slack\.com/ },
    {
        kind: "instagram",
        caption: "Instagram",
        icon: "instagram",
        regex: /^(?:https?:\/\/)?(?:[^./]+\.)?instagram\.com/,
    },
    { kind: "telephone", title: m => m[1], caption: "Phone", icon: "phone", regex: /^tel:(.+)/ },
    { kind: "message", title: m => m[1], caption: "Message", icon: "message-circle", regex: /^sms:(.+)/ },
];

// Returns an object
// {
//   kind: "youTube",
//   caption?: "YouTube",
//   link: "youtube",
//   title: "My great video"
// }
class ClassifyLinkValueProducer<O, A> extends RecomputingFromOneValueProducer<O, A> {
    protected recomputeFromInput(link: Value<O, A>): PotentialValue<O, A> {
        if (link === undefined) return undefined;

        const url = asString(link);

        for (const r of linkRecognizers) {
            const match = url.match(r.regex);
            if (match !== null) {
                const title =
                    r.title === undefined ? new PageTitleValueProducer<O, A>(url) : new Data<O, A>(r.title(match));
                return {
                    kind: new Data<O, A>(r.kind),
                    caption: new Data<O, A>(r.caption),
                    icon: new Data<O, A>(r.icon),
                    title,
                };
            }
        }
        return {
            kind: new Data<O, A>("unknown"),
            icon: new Data<O, A>("link"),
            title: new PageTitleValueProducer(url),
        };
    }
}

function extractIframeElement(maybeIframe: string): HTMLIFrameElement | undefined {
    try {
        const parser = new DOMParser();
        const doc = parser.parseFromString(maybeIframe, "text/html");
        return doc.getElementsByTagName("iframe")[0];
    } catch {
        return undefined;
    }
}

class ExtractIframeValueProducer<O, A> extends RecomputingFromOneValueProducer<O, A> {
    protected recomputeFromInput(linkOrIframe: Value<O, A>): PotentialValue<O, A> {
        if (linkOrIframe === undefined) {
            return {
                source: new Data<O, A>(undefined),
                height: new Data<O, A>(undefined),
                scrollable: new Data<O, A>(true),
            };
        }

        const possibleFragment = asString(linkOrIframe);
        const possibleIframe = extractIframeElement(possibleFragment);
        if (possibleIframe === undefined) {
            return {
                source: new Data<O, A>(possibleFragment),
                height: new Data<O, A>(undefined),
                scrollable: new Data<O, A>(true),
            };
        }

        const sourceAttr = possibleIframe.getAttribute("src") ?? possibleFragment;
        const heightAttr = definedMap(possibleIframe.getAttribute("height") ?? undefined, asMaybeNumber);
        const scrollableAttr =
            definedMap(possibleIframe.getAttribute("scrollable") ?? undefined, v => v !== "no") ?? true;
        return {
            source: new Data<O, A>(sourceAttr),
            height: new Data<O, A>(heightAttr),
            scrollable: new Data<O, A>(scrollableAttr),
        };
    }
}

class ShortenLinkValueProducer<O, A> extends RecomputingFromOneValueProducer<O, A> {
    protected recomputeFromInput(link: Value<O, A>): PotentialValue<O, A> {
        if (link === undefined) return undefined;
        return tryShortenLinkToHostname(asString(link));
    }
}

class GetLastURLPathComponentValueProducer<O, A> extends RecomputingFromOneValueProducer<O, A> {
    protected recomputeFromInput(link: Value<O, A>): PotentialValue<O, A> {
        if (link === undefined) return undefined;
        const url = parseURL(asString(link));
        if (url === undefined) return undefined;
        const components = url.pathname.split("/");
        for (let i = components.length - 1; i >= 0; i--) {
            const c = components[i];
            if (c !== "") {
                return c;
            }
        }
        return undefined;
    }
}

class ExtractYouTubeVideoIDValueProducer<O, A> extends RecomputingFromOneValueProducer<O, A> {
    protected recomputeFromInput(link: Value<O, A>): PotentialValue<O, A> {
        if (link === undefined) return undefined;

        const maybeURL = asString(link);

        const match = maybeURL.match(youTubeRegex);
        if (match !== null) {
            return match[1];
        }
        return maybeURL;
    }
}

class MakeTelURLValueProducer<O, A> extends RecomputingFromOneValueProducer<O, A> {
    protected recomputeFromInput(input: Value<O, A>): PotentialValue<O, A> {
        return makeTelephoneURL(asString(input));
    }
}

class MakeSMSURLValueProducer<O, A> extends RecomputingFromOneOrTwoValueProducer<O, A> {
    protected recomputeFromInputs(number: Value<O, A>, body: Value<O, A>): PotentialValue<O, A> {
        return makeSMSURL(asString(number), asMaybeString(body));
    }
}

class URLImageSourceValueProducer<O, A> extends ForwardingValueProducer<O, A> {
    constructor(_appEnvironment: A, private readonly _input: ValueProducer<O, A>) {
        super();
    }

    protected recomputeForwardee(): ValueProducer<O, A> | Loading | undefined {
        return this._input;
    }
}

class URLArrayImageSourceValueProducer<O, A> extends ForwardingValueProducer<O, A> {
    constructor(_appEnvironment: A, private readonly _input: ValueProducer<O, A>) {
        super();
    }

    protected recomputeForwardee(): ValueProducer<O, A> | Loading | undefined {
        return this._input;
    }
}

// This is not used anymore
class MapFromGeoCoordinatesImageSourceValueProducer<O, A> extends ForwardingValueProducer<O, A> {
    constructor(_appEnvironment: A, private readonly _input: ValueProducer<O, A>) {
        super();
    }

    protected recomputeForwardee(): ValueProducer<O, A> | Loading | undefined {
        return this._input;
    }
}

class MapFromAddressImageSourceValueProducer<O, A> extends RecomputingFromOneValueProducer<O, A> {
    protected recomputeFromInput(address: Value<O, A>): PotentialValue<O, A> {
        if (address === undefined) return undefined;
        return { address: new Data(address) };
    }
}

let mockedOldComputationModelDate: Date | undefined;
export function mockOldComputationModelDate(value: Date | undefined): void {
    mockedOldComputationModelDate = value;
}

class CurrentTimestampValueProducer<O, A> extends ImmutableSubscribableValueProducer<O, A> {
    constructor() {
        super();

        // eslint-disable-next-line glide/no-set-interval-without-assign
        setInterval(() => this.setListenersDirty(), 10 * 1000);
    }

    public get current(): Value<O, A> {
        return mockedOldComputationModelDate ?? getNowTimeZoneAgnostic();
    }

    public getObjectInspector(): ObjectInspector<O, A> | Loading | undefined {
        return undefined;
    }
}

class CurrentTimestampCallable<O, A> extends Callable<O, A> {
    private static _valueProducer: ValueProducer<any, any> | undefined;

    public call(): ValueProducer<O, A> {
        if (CurrentTimestampCallable._valueProducer === undefined) {
            CurrentTimestampCallable._valueProducer = new CurrentTimestampValueProducer();
        }
        return CurrentTimestampCallable._valueProducer;
    }
}

let mockedOldComputationModelUUID: string | undefined = undefined;
export function mockOldComputationModelUUID(value: string | undefined) {
    mockedOldComputationModelUUID = value;
}

class UniqueIdentifierValueProducer<O, A> extends ImmutableSubscribableValueProducer<O, A> {
    private readonly _value = mockedOldComputationModelUUID ?? uuid.v4();

    public get current(): Value<O, A> {
        return this._value;
    }

    public getObjectInspector(): ObjectInspector<O, A> | Loading | undefined {
        return undefined;
    }
}

class StartOfDayValueProducer<O, A> extends RecomputingFromOneValueProducer<O, A> {
    protected recomputeFromInput(dateTime: Value<O, A>): PotentialValue<O, A> {
        if (!(dateTime instanceof Date)) return undefined;
        return makeStartOfDayTimeZoneAgnostic(dateTime);
    }
}

class EndOfDayValueProducer<O, A> extends RecomputingFromOneValueProducer<O, A> {
    protected recomputeFromInput(dateTime: Value<O, A>): PotentialValue<O, A> {
        if (!(dateTime instanceof Date)) return undefined;
        return makeEndOfDayTimeZoneAgnostic(dateTime);
    }
}

class FormatNumberFixedValueProducer<O, A> extends RecomputingFromThreeOrFourValueProducer<O, A> {
    protected recomputeFromInputs(
        value: Value<O, A>,
        decimalsAfterPoint: Value<O, A>,
        withGroupSeparator: Value<O, A>,
        currency: PotentialValue<O, A>
    ): PotentialValue<O, A> {
        const number = asMaybeNumber(value);
        if (number === undefined || isNaN(number)) return undefined;
        if (decimalsAfterPoint === undefined) return value;

        return formatNumberFixed(
            number,
            asNumber(decimalsAfterPoint),
            definedMap(withGroupSeparator, asBoolean) ?? false,
            loadedDefinedMap(currency, asString)
        );
    }
}

class FormatDateTimeValueProducer<O, A> extends RecomputingFromThreeValueProducer<O, A> {
    protected recomputeFromInputs(
        value: Value<O, A>,
        dateFormat: Value<O, A>,
        timeFormat: Value<O, A>
    ): PotentialValue<O, A> {
        if (value === undefined) return undefined;

        const dateTZA = asDate(value);
        if (dateTZA === undefined) return undefined;

        return formatDateTime(dateTZA, dateFormat as DateFormat, timeFormat as TimeFormat);
    }
}

class GeoDistanceValueProducer<O, A> extends RecomputingFromTwoWithDependenciesValueProducer<O, A> {
    protected recomputeFromInputs(
        _first: Value<O, A>,
        _second: Value<O, A>,
        force: ForceValueProducer<O, A>
    ): PotentialValue<O, A> {
        const a = deconstructLatLong(this._first, force);
        const b = deconstructLatLong(this._second, force);
        if (a === LOADING || b === LOADING) return LOADING;
        if (a === undefined || b === undefined) return undefined;

        return getDistanceFromLatLonInKm(a.latitude, a.longitude, b.latitude, b.longitude);
    }
}

class GenerateImageValueProducer<O, A> extends RecomputingFromTwoValueProducer<O, A> {
    protected recomputeFromInputs(inputValue: Value<O, A>, imageKindValue: Value<O, A>): PotentialValue<O, A> {
        if (inputValue === undefined || imageKindValue === undefined) return undefined;

        const input = asString(inputValue);
        if (input === "") return undefined;

        const imageKind = asString(imageKindValue) as GeneratedImageKind;
        if (imageKind === GeneratedImageKind.Triangles) {
            return generateImageFromSeed(input, false);
        } else if (imageKind === GeneratedImageKind.Mesh) {
            return generateImageFromSeed(input, true);
        }
        return undefined;
    }
}

class ConstructURLValueProducer<O, A> extends RecomputingValueProducer<O, A> {
    constructor(
        _appEnvironment: A,
        private readonly _scheme: ValueProducer<O, A> | undefined,
        private readonly _host: ValueProducer<O, A> | undefined,
        private readonly _path: ValueProducer<O, A> | undefined,
        private readonly _params: ValueProducer<O, A>
    ) {
        super();
    }

    public recompute(force: ForceValueProducer<O, A>): PotentialValue<O, A> {
        const schemeValue = definedMap(this._scheme, force);
        const hostValue = definedMap(this._host, force);
        const pathValue = definedMap(this._path, force);
        const paramsValue = definedMap(this._params, force);
        if (schemeValue === LOADING || hostValue === LOADING || pathValue === LOADING || paramsValue === LOADING) {
            return LOADING;
        }
        if (hostValue === undefined) return undefined;

        let scheme = definedMap(schemeValue, asString)?.trim();
        if (scheme === undefined || scheme === "") {
            scheme = "https";
        }

        const host = asString(hostValue);
        if (host === "") return undefined;

        let path = definedMap(pathValue, asString)?.trim();
        if (path === undefined) {
            path = "/";
        } else if (!path.startsWith("/")) {
            path = "/" + path;
        }

        const { replacements } = forceReplacements(asArrayValue(paramsValue), force);
        let haveLoading = false;
        const query = mapFilterUndefined(replacements.entries(), ([k, vp]) => {
            const v = force(vp);
            if (v === LOADING) {
                haveLoading = true;
            }
            if (!isLoadedDefined(v)) return undefined;
            return `${encodeURIComponent(k)}=${encodeURIComponent(asString(v))}`;
        }).join("&");
        if (haveLoading) return LOADING;

        let url = `${scheme}://${host}${path}`;
        if (query.length > 0) {
            url = url + "?" + query;
        }

        return url;
    }
}

export function registerFunctions(): void {
    registerDeserializable(FunctionCallable);
    registerDeserializable(LookupBuiltinCallable);

    registerBuiltinFunction("AreEqual", new BuiltinCallable(AreEqualValueProducer));
    registerBuiltinFunction("AreIdentical", new BuiltinCallable(AreIdenticalValueProducer));
    registerBuiltinFunction("IsLessThan", new BuiltinCallable(IsLessThanValueProducer));
    registerBuiltinFunction("IsGreaterThan", new BuiltinCallable(IsGreaterThanValueProducer));
    registerBuiltinFunction("IsGreaterOrEqualTo", new BuiltinCallable(IsGreaterOrEqualToValueProducer));
    registerBuiltinFunction("IsLessOrEqualTo", new BuiltinCallable(IsLessOrEqualToValueProducer));
    registerBuiltinFunction("IsDefined", new BuiltinCallable(IsDefinedValueProducer));
    registerBuiltinFunction("IsArray", new BuiltinCallable(IsArrayValueProducer));
    registerBuiltinFunction("IsNonEmptyArray", new BuiltinCallable(IsNonEmptyArrayValueProducer));
    registerBuiltinFunction("ArrayContains", new BuiltinCallable(ArrayContainsValueProducer));
    registerBuiltinFunction("ArraysOverlap", new BuiltinCallable(ArraysOverlapValueProducer));
    registerBuiltinFunction("JoinStrings", new BuiltinCallable(JoinStringsValueProducer));
    registerBuiltinFunction("ReplaceStrings", new BuiltinCallable(ReplaceStringsValueProducer));
    registerBuiltinFunction("ListLength", new BuiltinCallable(ListLengthValueProducer));
    registerBuiltinFunction("ListNth", new BuiltinCallable(ListNthValueProducer));
    registerBuiltinFunction("ListNthLast", new BuiltinCallable(ListNthLastValueProducer));
    registerBuiltinFunction("ListTake", new BuiltinCallable(ListTakeValueProducer));
    registerBuiltinFunction("ListFromObject", new BuiltinCallable(ListFromObjectValueProducer));
    registerBuiltinFunction("ReverseList", new BuiltinCallable(ReverseListValueProducer));
    registerBuiltinFunction("JoinLists", new BuiltinCallable(JoinListsValueProducer));
    registerBuiltinFunction("Random", new BuiltinCallable(makeRandomMathValueProducer(Math.random)));
    registerBuiltinFunction("Negate", new BuiltinCallable(makeUnaryMathValueProducer(x => -x)));
    registerBuiltinFunction("Absolute", new BuiltinCallable(makeUnaryMathValueProducer(Math.abs)));
    registerBuiltinFunction("Floor", new BuiltinCallable(makeUnaryMathValueProducer(Math.floor)));
    registerBuiltinFunction("Ceiling", new BuiltinCallable(makeUnaryMathValueProducer(Math.ceil)));
    registerBuiltinFunction("Round", new BuiltinCallable(RoundValueProducer));
    registerBuiltinFunction("Sign", new BuiltinCallable(makeUnaryMathValueProducer(Math.sign)));
    registerBuiltinFunction("Truncate", new BuiltinCallable(TruncateValueProducer));
    registerBuiltinFunction("Sine", new BuiltinCallable(makeUnaryMathValueProducer(Math.sin)));
    registerBuiltinFunction("Cosine", new BuiltinCallable(makeUnaryMathValueProducer(Math.cos)));
    registerBuiltinFunction("Tangent", new BuiltinCallable(makeUnaryMathValueProducer(Math.tan)));
    registerBuiltinFunction("ArcSine", new BuiltinCallable(makeUnaryMathValueProducer(Math.asin)));
    registerBuiltinFunction("ArcCosine", new BuiltinCallable(makeUnaryMathValueProducer(Math.acos)));
    registerBuiltinFunction("ArcTangent", new BuiltinCallable(makeUnaryMathValueProducer(Math.atan)));
    registerBuiltinFunction("Add", new BuiltinCallable(AddValueProducer));
    registerBuiltinFunction("AddToConstant", new BuiltinCallable(makeBinaryMathValueProducer((x, y) => x + y)));
    registerBuiltinFunction("Subtract", new BuiltinCallable(SubtractValueProducer));
    registerBuiltinFunction("SubtractFromConstant", new BuiltinCallable(SubtractFromConstantValueProducer));
    const MultiplyValueProducer = makeBinaryMathValueProducer((x, y) => x * y);
    registerBuiltinFunction("Multiply", new BuiltinCallable(MultiplyValueProducer));
    registerBuiltinFunction("MultiplyWithConstant", new BuiltinCallable(MultiplyValueProducer));
    const DivideValueProducer = makeBinaryMathValueProducer((x, y) => x / y);
    registerBuiltinFunction("Divide", new BuiltinCallable(DivideValueProducer));
    registerBuiltinFunction("DivideConstantBy", new BuiltinCallable(DivideValueProducer));
    registerBuiltinFunction("Modulo", new BuiltinCallable(ModuloValueProducer));
    registerBuiltinFunction("Minimum2", new BuiltinCallable(makeBinaryMathValueProducer(Math.min)));
    registerBuiltinFunction("Maximum2", new BuiltinCallable(makeBinaryMathValueProducer(Math.max)));
    registerBuiltinFunction("Power", new BuiltinCallable(makeBinaryMathValueProducer(Math.pow)));
    registerBuiltinFunction("Logarithm", new BuiltinCallable(LogarithmValueProducer));
    registerBuiltinFunction("ArcTangent2", new BuiltinCallable(makeBinaryMathValueProducer(Math.atan2)));
    registerBuiltinFunction("SubtractDates", new BuiltinCallable(SubtractDatesValueProducer));
    registerBuiltinFunction("AddDurationToDate", new BuiltinCallable(AddDurationToDateValueProducer));
    registerBuiltinFunction("And", new BuiltinCallable(AndValueProducer));
    registerBuiltinFunction("Or", new BuiltinCallable(OrValueProducer));
    registerBuiltinFunction("Not", new BuiltinCallable(NotValueProducer));
    registerBuiltinFunction("WithDefault", new BuiltinCallable(WithDefaultValueProducer));
    registerBuiltinFunction("PrimitiveDiode", new BuiltinCallable(PrimitiveDiodeValueProducer));
    registerBuiltinFunction("WriteOncePrimitiveDiode", new BuiltinCallable(WriteOncePrimitiveDiodeValueProducer));
    registerBuiltinFunction("StringToNumber", new BuiltinCallable(StringToNumberValueProducer));
    registerBuiltinFunction("GetLocalizedString", new BuiltinCallable(GetLocalizedStringValueProducer));
    registerBuiltinFunction("FormatLocalizedString", new BuiltinCallable(FormatLocalizedStringValueProducer));
    registerBuiltinFunction("SplitString", new BuiltinCallable(SplitStringValueProducer));
    registerBuiltinFunction("ExtractInitials", new BuiltinCallable(ExtractInitialsValueProducer));
    registerBuiltinFunction("ParseUserDateTime", new BuiltinCallable(ParseUserDateTimeValueProducer));
    registerBuiltinFunction("DecomposeDateTime", new BuiltinCallable(DecomposeDateTimeValueProducer));
    registerBuiltinFunction("ConvertToBoolean", new BuiltinCallable(ConvertToBooleanValueProducer));
    registerBuiltinFunction("ConvertToNumber", new BuiltinCallable(ConvertToNumberValueProducer));
    registerBuiltinFunction("ConvertToString", new BuiltinCallable(ConvertToStringValueProducer));
    registerBuiltinFunction(
        "ConvertToLocaleAgnosticString",
        new BuiltinCallable(ConvertToLocaleAgnosticStringValueProducer)
    );
    registerBuiltinFunction("ConvertToDateTime", new BuiltinCallable(ConvertToDateTimeValueProducer));
    registerBuiltinFunction("ContainsString", new BuiltinCallable(ContainsStringValueProducer));
    registerBuiltinFunction(
        "StringsAreLowercaseTrimEqual",
        new BuiltinCallable(StringsAreLowercaseTrimEqualValueProducer)
    );
    registerBuiltinFunction("AreEmailsEqual", new BuiltinCallable(AreEmailsEqualValueProducer));
    registerBuiltinFunction("MatchesEmailFilter", new BuiltinCallable(MatchesEmailFilterValueProducer));
    registerBuiltinFunction("StringEmpty", new BuiltinCallable(StringEmptyValueProducer));
    registerBuiltinFunction("IsNotEmpty", new BuiltinCallable(IsNotEmptyValueProducer));
    registerBuiltinFunction("StringsNotEmpty", new BuiltinCallable(StringsNotEmptyValueProducer));
    registerBuiltinFunction("StringsAreNumbersOrEmpty", new BuiltinCallable(StringsAreNumbersOrEmptyValueProducer));
    registerBuiltinFunction("TrimString", new BuiltinCallable(TrimStringValueProducer));
    registerBuiltinFunction(
        "ObjectHasNonEmptyProperties",
        new BuiltinCallable(ObjectHasNonEmptyPropertiesValueProducer)
    );
    registerBuiltinFunction("AllObjectPropertiesAreTrue", new BuiltinCallable(AllObjectPropertiesAreTrueValueProducer));
    registerBuiltinFunction("PropertiesContainString", new BuiltinCallable(PropertiesContainStringValueProducer));
    registerBuiltinFunction("ExtractChangedProperties", new BuiltinCallable(ExtractChangedPropertiesValueProducer));
    registerBuiltinFunction("Shuffle", new BuiltinCallable(ShuffleValueProducer));
    registerBuiltinFunction("RandomPick", new BuiltinCallable(RandomPickValueProducer));
    registerBuiltinFunction("ClassifyLink", new BuiltinCallable(ClassifyLinkValueProducer));
    registerBuiltinFunction("ExtractIframe", new BuiltinCallable(ExtractIframeValueProducer));
    registerBuiltinFunction("ShortenLink", new BuiltinCallable(ShortenLinkValueProducer));
    registerBuiltinFunction("GetLastURLPathComponent", new BuiltinCallable(GetLastURLPathComponentValueProducer));
    registerBuiltinFunction("ExtractYouTubeVideoID", new BuiltinCallable(ExtractYouTubeVideoIDValueProducer));
    registerBuiltinFunction("MakeTelURL", new BuiltinCallable(MakeTelURLValueProducer));
    registerBuiltinFunction("MakeSMSURL", new BuiltinCallable(MakeSMSURLValueProducer));
    registerBuiltinFunction("URLImageSource", new BuiltinCallable(URLImageSourceValueProducer));
    registerBuiltinFunction("URLArrayImageSource", new BuiltinCallable(URLArrayImageSourceValueProducer));
    registerBuiltinFunction(
        "MapFromGeoCoordinatesImageSource",
        new BuiltinCallable(MapFromGeoCoordinatesImageSourceValueProducer)
    );
    registerBuiltinFunction("MapFromAddressImageSource", new BuiltinCallable(MapFromAddressImageSourceValueProducer));
    registerBuiltinFunction("CurrentTimestamp", new CurrentTimestampCallable());
    registerBuiltinFunction("UniqueIdentifier", new BuiltinCallable(UniqueIdentifierValueProducer));
    registerBuiltinFunction("StartOfDay", new BuiltinCallable(StartOfDayValueProducer));
    registerBuiltinFunction("EndOfDay", new BuiltinCallable(EndOfDayValueProducer));
    registerBuiltinFunction("FormatNumberFixed", new BuiltinCallable(FormatNumberFixedValueProducer));
    registerBuiltinFunction("FormatDateTime", new BuiltinCallable(FormatDateTimeValueProducer));
    registerBuiltinFunction("GeoDistance", new BuiltinCallable(GeoDistanceValueProducer));
    registerBuiltinFunction("GenerateImage", new BuiltinCallable(GenerateImageValueProducer));
    registerBuiltinFunction("ConstructURL", new BuiltinCallable(ConstructURLValueProducer));
}
